import {
  Select as MUISelect,
  SelectProps as MUISelectProps,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { useSelectStyles } from './useSelectStyles';

interface SelectProps<Value> extends MUISelectProps<Value> {}

export function Select<Value>({
  MenuProps,
  classes: classesOverrides,
  ...props
}: SelectProps<Value>) {
  const [isOpen, setIsOpen] = useState(false);

  const { classes, cx } = useSelectStyles();

  /* this useEffect is necessary for handling the select visibility on page scrolling */
  /* https://github.com/mui/material-ui/issues/17353#issuecomment-1002113498 */
  useEffect(() => {
    const handler = () => {
      setIsOpen(false);
    };

    window.addEventListener('scroll', handler);

    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, []);

  // caller-supplied menu and slot classes are merged with the shared ones so
  // that overriding one detail does not drop the shared select styling
  const menuProps = useMemo(
    (): MUISelectProps['MenuProps'] => ({
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      disableScrollLock: true,
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
      },
      ...MenuProps,
      classes: {
        ...MenuProps?.classes,
        paper: cx(classes.paper, MenuProps?.classes?.paper),
      },
    }),
    [MenuProps, classes.paper, cx],
  );

  const mergedClasses = useMemo(
    (): MUISelectProps['classes'] => ({
      ...classesOverrides,
      root: cx(classes.root, classesOverrides?.root),
      select: cx(classes.select, classesOverrides?.select),
    }),
    [classes.root, classes.select, classesOverrides, cx],
  );

  return (
    <MUISelect
      MenuProps={menuProps}
      classes={mergedClasses}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      size="small"
      {...props}
    />
  );
}
